import type { GuidePage } from '../guides';

const example = [
  { item: 'Materials at cost: 320 sq ft of pavers, 6 tons of base, sand, edge restraint, delivery', amount: '$2,640' },
  { item: 'Material markup to cover handling, waste and returns', amount: '$400' },
  { item: 'Labor: 3-person crew x 2.5 days at loaded hourly cost', amount: '$2,880' },
  { item: 'Equipment: compact track loader and plate compactor for 2 days, fuel and trailer', amount: '$520' },
  { item: 'Overhead share and the margin chosen for hardscape work', amount: '$1,460' },
];

const faq = [
  {
    q: 'Should landscapers mark up materials?',
    a: 'Many do, to cover the time spent ordering, picking up, handling, storing and returning materials, and the waste on every job. Whether you mark up or build that cost into labor is your choice, but it has to be covered somewhere.',
  },
  {
    q: 'How do I estimate labor for a landscape job?',
    a: 'Break the job into tasks you have timed before: excavation per square foot, base installation, laying pavers, planting per plant size, mulching per cubic yard. Add the tasks up, then add time for setup, cleanup and the unexpected, such as rock or roots.',
  },
  {
    q: 'Is pricing by the square foot accurate for hardscape?',
    a: 'It is a useful check once you know your own costs, but access, excavation depth, cuts around curves and steps can double the time for the same area. Build the price from tasks, then compare with your usual per-square-foot figure.',
  },
  {
    q: 'How should I price plant installs?',
    a: 'Plant cost plus the labor to dig, amend soil, plant and water, which depends heavily on container size. A 15-gallon tree takes many times the effort of a 1-gallon perennial. Decide whether a plant warranty is included and price it in if so.',
  },
];

const page: GuidePage = {
  slug: 'how-to-price-landscaping',
  path: '/resources/how-to-price-landscaping',
  primaryKeyword: 'how to price landscaping jobs',
  secondaryKeywords: ['landscaping pricing guide', 'how to price hardscape jobs', 'landscape estimating'],
  title: 'How to Price Landscaping and Hardscape Jobs | Scopie', // 52
  metaDescription:
    'Price landscaping from materials, markup, crew days, equipment, overhead and margin, with a worked paver patio example and the mistakes that eat profit.', // 152
  h1: 'How to price landscaping jobs',
  answerFirst:
    'Price landscaping by adding up materials at cost, a markup or handling allowance, crew time built from tasks you have timed, equipment, overhead and your margin. Check the result against your usual per-square-foot figures, then adjust for access, depth and complexity. The paver patio example below shows each step.',
  datePublished: '2026-10-07',
  lastReviewed: '2026-10-07',
  schemaType: 'Article',
  blocks: [
    {
      type: 'list',
      id: 'build-up',
      h2: 'Build the price up in this order',
      style: 'steps',
      items: [
        { title: 'Take off materials', text: 'Quantities in your supplier\'s units: tons of base, square feet of pavers, cubic yards of mulch, plants by container size.' },
        { title: 'Add a material allowance', text: 'Markup or handling cost for ordering, pickup, waste and returns.' },
        { title: 'Time the tasks', text: 'Excavate, base, lay, cut, compact, plant, mulch, clean up. Use your own past jobs.' },
        { title: 'Add equipment', text: 'Owned machines carry a daily cost too: fuel, maintenance, depreciation, the trailer.' },
        { title: 'Overhead and margin', text: 'A fair share of insurance, yard, trucks and office, then the profit you want for this kind of work.' },
      ],
    },
    {
      type: 'sample-quote',
      id: 'example',
      h2: 'Worked example: a 320 sq ft paver patio',
      label: 'How the patio price is built (internal costing)',
      lines: example,
      total: '$7,900',
      totalLabel: 'Price to quote',
      note: 'Illustrative figures. Material prices and labor rates vary by region; use your own.',
    },
    {
      type: 'text',
      id: 'site',
      h2: 'What the site does to the number',
      lead: 'Same patio, two backyards, very different jobs.',
      paragraphs: [
        'A wide gate and flat lawn let a track loader do the digging in a morning. A narrow side yard and a slope mean wheelbarrows and an extra day or two. Clay soil may need deeper excavation and more base; a tree root zone may limit how deep you can go. Walk the site with those questions in mind before you price.',
        'Softscape has its own swings: hard, rocky soil slows planting, and a bed that needs grading or new topsoil is a different job from a bed that only needs mulch.',
      ],
    },
    {
      type: 'list',
      id: 'mistakes',
      h2: 'Where landscaping prices lose money',
      style: 'mistakes',
      items: [
        { title: 'Materials at cost with nothing for handling', text: 'Every trip to the yard and every returned pallet is unpaid time.' },
        { title: 'Equipment treated as free', text: 'A machine you own still costs money each day it runs.' },
        { title: 'One crew rate for every task', text: 'Hardscape and planting go at different speeds; price them separately.' },
        { title: 'Old material prices', text: 'Stone and plant prices move; refresh your numbers each season and date your quotes.' },
      ],
    },
    {
      type: 'text',
      id: 'quote',
      h2: 'Presenting it to the homeowner',
      paragraphs: [
        'The homeowner does not need your internal costing, but they do need to see the parts of the job: base, pavers, planting, mulch, haul-away. In Scopie you build the quote from your saved items, add options such as lighting as optional lines, and email it. The homeowner ticks the options they want and signs.',
      ],
      links: [{ phrase: 'build the quote from your saved items', href: '/for/landscaping' }],
    },
    { type: 'faq', id: 'faq', h2: 'Landscaping pricing questions', items: faq },
  ],
  faq,
  internalLinks: [
    { href: '/for/landscaping', anchor: 'Scopie for landscaping businesses', status: 'live' },
    { href: '/templates/landscaping-quote-template', anchor: 'landscaping quote template', status: 'live' },
    { href: '/resources', anchor: 'All pricing guides', status: 'live' },
  ],
  cta: {
    heading: 'Quote the patio while you are still on site',
    text: 'Save your materials, tasks and options in Scopie, and email each landscaping quote the same day.',
    button: 'Start free',
    href: '/register',
    fine: '14-day free trial. No credit card required.',
  },
};

export default page;
